import { useEffect, useState, useRef, forwardRef } from "react";
import {
	Typography,
	Box,
	Chip,
	CircularProgress,
	Divider,
	Button,
	LinearProgress,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormControl,
	Tooltip,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import {
	CheckCircle as SuccessIcon,
	Warning as WarningIcon,
	Error as ErrorIcon,
	Build as BuildIcon,
	SwapHoriz as SwapIcon,
	Info as InfoIcon,
	LocalShipping as TruckIcon,
} from "@material-ui/icons";
import { createRequest } from "app/services/requestUtil";
import { incrementDataRevision } from "app/store/tools/revisionSlice";
import { useDispatch } from "react-redux";

const WARNING_LABELS = {
	LONGHAUL_NOT_PARTNER: "LineHaul Mismatch",
	TRUCK_CONFLICT_SAME_POSITION: "Truck Conflict",
	CHILD_TRIP_OUTSIDE_SCHEDULE: "Outside Schedule",
	POSSIBLE_CANCELLATION: "Possible Cancellation",
	NON_ACTIVE_RELAY_TRUCK: "Non-Active Truck",
};

const WARNING_COLORS = {
	LONGHAUL_NOT_PARTNER: { color: "#0277bd", bg: "#e1f5fe" },
	TRUCK_CONFLICT_SAME_POSITION: { color: "#c62828", bg: "#ffebee" },
	CHILD_TRIP_OUTSIDE_SCHEDULE: { color: "#ef6c00", bg: "#fff3e0" },
	POSSIBLE_CANCELLATION: { color: "#d32f2f", bg: "#ffebee" },
	NON_ACTIVE_RELAY_TRUCK: { color: "#6a1b9a", bg: "#f3e5f5" },
};

// Sub-reason labels for POSSIBLE_CANCELLATION (carried in warning.metadata.reason).
// Mirrors the backend reason codes set in relayScheduleManager.js.
const POSSIBLE_CANCELLATION_REASON_LABELS = {
	LONGHAUL_TRUCK_NOT_AT_YARD: "LH truck not at yard",
	ASSIGNED_TRUCK_UNAVAILABLE: "Assigned truck unavailable",
};

// Build a concise sub-label and tooltip detail for POSSIBLE_CANCELLATION using
// metadata.reason and metadata.unavailable_assignments. ASSIGNED_TRUCK_UNAVAILABLE
// emits one entry per affected position (PICKUP / LONGHAUL / DELIVERY) so the
// dialog can call out exactly which slot is on an unavailable truck.
const formatPossibleCancellationMetadata = (warning) => {
	const reason = warning?.metadata?.reason;
	const reasonLabel = reason ? POSSIBLE_CANCELLATION_REASON_LABELS[reason] || reason.replace(/_/g, " ") : null;
	const assignments = Array.isArray(warning?.metadata?.unavailable_assignments)
		? warning.metadata.unavailable_assignments
		: [];
	const assignmentsLabel = assignments.length
		? assignments.map((a) => `${a.position} #${a.truckLabel || a.truckId}${a.date ? ` on ${a.date}` : ""}`).join(", ")
		: null;
	let subLabel = null;
	if (reason === "ASSIGNED_TRUCK_UNAVAILABLE") {
		subLabel = assignmentsLabel ? `Unavailable: ${assignmentsLabel}` : reasonLabel;
	} else if (reasonLabel) {
		subLabel = reasonLabel;
	}
	const tooltipDetailParts = [];
	if (reasonLabel) tooltipDetailParts.push(reasonLabel);
	if (assignmentsLabel) tooltipDetailParts.push(assignmentsLabel);
	return {
		subLabel,
		tooltipDetail: tooltipDetailParts.length ? tooltipDetailParts.join(" — ") : null,
	};
};

// Render a list of warning chips with per-warning sub-labels and tooltips. Used
// for both the single-chain CONFIRM view and the per-chain multi-chain view.
const renderWarningChips = (warningList) => {
	if (!warningList || warningList.length === 0) return null;
	return warningList.map((w, i) => {
		const meta = WARNING_COLORS[w.code] || { color: "#ff9800", bg: "#fff8e1" };
		const baseLabel = WARNING_LABELS[w.code] || w.code?.replace(/_/g, " ");
		const isPossibleCancellation = w.code === "POSSIBLE_CANCELLATION";
		const { subLabel, tooltipDetail } = isPossibleCancellation ? formatPossibleCancellationMetadata(w) : {};
		const tooltipText = isPossibleCancellation
			? tooltipDetail
				? w.message
					? `${w.message}\n${tooltipDetail}`
					: `${baseLabel} — ${tooltipDetail}`
				: w.message || baseLabel
			: w.message || baseLabel;
		const chip = (
			<Chip
				size="small"
				label={subLabel ? `${baseLabel}: ${subLabel}` : baseLabel}
				style={{ backgroundColor: meta.bg, color: meta.color, margin: 2, fontWeight: 500 }}
				icon={<WarningIcon style={{ color: meta.color, fontSize: "0.9rem" }} />}
			/>
		);
		return tooltipText && tooltipText !== baseLabel ? (
			<Tooltip key={i} title={tooltipText}>
				<span>{chip}</span>
			</Tooltip>
		) : (
			<span key={i}>{chip}</span>
		);
	});
};

const RelayChainFixWarningsView = forwardRef(({ dataIds, setTitle, setSize, onDone, onClose }, _) => {
	const dispatch = useDispatch();
	const { carrierId, chainId, chain, chains: dayChains, date: dayDate } = dataIds || {};

	const [phase, setPhase] = useState("CONFIRM");
	const [report, setReport] = useState(null);
	const [error, setError] = useState(null);
	const [showLog, setShowLog] = useState(false);
	const [fixScope, setFixScope] = useState("chain");
	const mounted = useRef(true);

	useEffect(() => {
		setTitle?.("Fix Chain Warnings");
		setSize?.("max-w-xl");
		return () => {
			mounted.current = false;
		};
		// eslint-disable-next-line
	}, []);

	// Multi-chain mode (from TripEditView day-level response)
	const isMultiChain = Array.isArray(dayChains) && dayChains.length > 0;

	// Single-chain fallback (from relayScheduleChainsList)
	const warnings = isMultiChain ? [] : chain?.coverage_warnings || [];
	const truckLabel = isMultiChain ? null : chain?.truck__view?.label || chain?.truck?.truckid || "N/A";
	const displayDate = dayDate || chain?.date || "N/A";

	const chainScheduleName =
		chain?.longhaul_schedules?.[0]?.schedule_name || chain?.pickup_schedules?.[0]?.schedule_name || null;
	const hasMultipleChains = isMultiChain ? dayChains.length > 1 : false;

	const executeFix = async (dryRun = false) => {
		setPhase(dryRun ? "ANALYZING" : "FIXING");
		setError(null);
		setReport(null);

		try {
			const url = `api/relay/carriers/${carrierId}/chains/${encodeURIComponent(chainId)}/fix-warnings`;
			const response = await createRequest(true, 120000)
				.post(url, { data: { dryRun, scope: fixScope } })
				.then((res) => res.data);

			if (!mounted.current) return;

			const data = response?.data || response;
			setReport(data);
			setPhase(dryRun ? "DRY_RUN_RESULT" : "RESULT");

			if (!dryRun && data.success) {
				dispatch(incrementDataRevision({ event: "profileRevision" }));
			}
		} catch (err) {
			if (!mounted.current) return;
			const msg = err?.response?.data?.errors?.[0]?.message || err?.message || "Unexpected error";
			setError(msg);
			setPhase("ERROR");
		}
	};

	const renderWarnings = () => (
		<Box mt={1} mb={1}>
			{renderWarningChips(warnings)}
		</Box>
	);

	const formatChainLabel = (p) => {
		const datePart = p.chainDate || "?";
		const namePart = p.chainScheduleName || "Schedule";
		const loadPart = p.chainLoadIndex !== null && p.chainLoadIndex !== undefined ? `#${p.chainLoadIndex}` : "";
		return `${datePart} · ${namePart}${loadPart ? ` ${loadPart}` : ""}`;
	};

	const renderPlan = (plan) => {
		if (!plan || plan.length === 0) return null;
		const planChainIds = [...new Set(plan.map((p) => p.chainId).filter(Boolean))];
		const isMultiChainPlan = planChainIds.length > 1;
		return (
			<Box mt={2}>
				<Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
					Reassignment Plan ({plan.length} swap{plan.length > 1 ? "s" : ""}
					{isMultiChainPlan ? ` across ${planChainIds.length} chains` : ""})
				</Typography>
				{plan.map((p, i) => (
					<Box
						key={i}
						style={{ padding: "6px 8px", backgroundColor: i % 2 === 0 ? "#f5f5f5" : "#fff", borderRadius: 4 }}
					>
						{isMultiChainPlan && p.chainId && p.chainId !== chainId && (
							<Typography
								variant="caption"
								style={{ color: "#7b1fa2", fontWeight: 500, display: "block", marginBottom: 2 }}
							>
								{formatChainLabel(p)}
							</Typography>
						)}
						<Box display="flex" alignItems="center">
							<Chip size="small" label={p.position} style={{ marginRight: 8, fontWeight: 600, minWidth: 80 }} />
							<Typography variant="body2" style={{ flex: 1 }}>
								{p.loadId ? `Load ${p.loadId}` : `Trip ${p.tripId?.substring(0, 12)}...`}
							</Typography>
							<SwapIcon style={{ color: "#666", margin: "0 6px", fontSize: "1rem" }} />
							<Typography variant="body2">
								<span style={{ color: "#c62828", textDecoration: "line-through" }}>{p.from}</span>
								{" → "}
								<span style={{ color: "#2e7d32", fontWeight: 600 }}>{p.to}</span>
							</Typography>
						</Box>
						{p.proximity && (
							<Box display="flex" alignItems="center" style={{ marginLeft: 88, marginTop: 2 }}>
								<TruckIcon style={{ fontSize: "0.85rem", color: "#1565c0", marginRight: 4 }} />
								<Typography variant="caption" style={{ color: "#1565c0", fontWeight: 500 }}>
									{p.proximity.emptyMiles} mi empty
								</Typography>
								{p.proximity.address && (
									<Typography variant="caption" style={{ color: "#666", marginLeft: 6 }}>
										{p.proximity.position === "PICKUP" ? "from" : "to"} {p.proximity.address}
									</Typography>
								)}
								{p.proximity.loadId && (
									<Typography variant="caption" style={{ color: "#888", marginLeft: 6 }}>
										(Load {p.proximity.loadId})
									</Typography>
								)}
							</Box>
						)}
					</Box>
				))}
			</Box>
		);
	};

	const renderActions = (actions) => {
		if (!actions || actions.length === 0) return null;
		return (
			<Box mt={2}>
				<Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
					Execution Results
				</Typography>
				{actions.map((a, i) => {
					if (a.type === "skipped" || a.type === "no_plan") {
						return (
							<Box key={i} display="flex" alignItems="center" style={{ padding: "4px 0" }}>
								<InfoIcon style={{ color: "#ff9800", fontSize: "1rem", marginRight: 6 }} />
								<Typography variant="body2" color="textSecondary">
									{a.reason}
								</Typography>
							</Box>
						);
					}
					const isSuccess = a.success;
					const hasChainMeta = a.chainScheduleName || a.chainDate;
					return (
						<Box key={i} style={{ padding: "4px 0" }}>
							{hasChainMeta && (
								<Typography
									variant="caption"
									style={{ color: "#7b1fa2", fontWeight: 500, display: "block", marginLeft: 22 }}
								>
									{formatChainLabel(a)}
								</Typography>
							)}
							<Box display="flex" alignItems="center">
								{isSuccess ? (
									<SuccessIcon style={{ color: "#4caf50", fontSize: "1rem", marginRight: 6 }} />
								) : (
									<ErrorIcon style={{ color: "#f44336", fontSize: "1rem", marginRight: 6 }} />
								)}
								<Chip size="small" label={a.position} style={{ marginRight: 8, minWidth: 70 }} />
								{(a.loadId || a.tripId) && (
									<Typography variant="body2" style={{ marginRight: 8, color: "#555" }}>
										{a.loadId ? `Load ${a.loadId}` : `Trip ${a.tripId?.substring(0, 12)}...`}
									</Typography>
								)}
								<Typography variant="body2">
									{a.from} → {a.to}
									{!isSuccess && a.error && <span style={{ color: "#f44336", marginLeft: 8 }}>({a.error})</span>}
								</Typography>
							</Box>
						</Box>
					);
				})}
			</Box>
		);
	};

	const renderVerification = (v) => {
		if (!v) return null;
		const allResolved = v.allChainsResolved !== undefined ? v.allChainsResolved : v.chainResolved;
		return (
			<Box mt={2} p={1.5} style={{ backgroundColor: allResolved ? "#e8f5e9" : "#fff3e0", borderRadius: 6 }}>
				<Box display="flex" alignItems="center">
					{allResolved ? (
						<SuccessIcon style={{ color: "#4caf50", marginRight: 6 }} />
					) : (
						<WarningIcon style={{ color: "#ff9800", marginRight: 6 }} />
					)}
					<Typography variant="subtitle2" style={{ fontWeight: 600 }}>
						{allResolved ? "All warnings resolved" : "Some warnings remain"}
					</Typography>
				</Box>
				{v.composition && (
					<Box mt={1} display="flex" style={{ gap: 12 }}>
						<Typography variant="caption">PU: {v.composition.pickup}</Typography>
						<Typography variant="caption">LH: {v.composition.longhaul}</Typography>
						<Typography variant="caption">DL: {v.composition.delivery}</Typography>
					</Box>
				)}
				{v.remainingWarnings?.length > 0 && (
					<Box mt={1}>
						{v.remainingWarnings.map((w, i) => (
							<Typography key={i} variant="caption" style={{ color: "#e65100", display: "block" }}>
								{w.code}: {w.message}
							</Typography>
						))}
					</Box>
				)}
				{v.chainResults && Object.keys(v.chainResults).length > 1 && (
					<Box mt={1}>
						<Typography variant="caption" style={{ fontWeight: 600 }}>
							Chain Results:
						</Typography>
						{Object.entries(v.chainResults).map(([cid, cr]) => (
							<Box key={cid} display="flex" alignItems="center" style={{ marginTop: 2, gap: 4 }}>
								{cr.resolved ? (
									<SuccessIcon style={{ color: "#4caf50", fontSize: "0.85rem" }} />
								) : (
									<WarningIcon style={{ color: "#ff9800", fontSize: "0.85rem" }} />
								)}
								<Typography variant="caption">
									{cid.substring(0, 30)}... {cr.resolved ? "resolved" : "unresolved"}
								</Typography>
							</Box>
						))}
					</Box>
				)}
			</Box>
		);
	};

	const renderLog = () => {
		if (!report?.log?.length) return null;
		return (
			<Box mt={2}>
				<Button size="small" onClick={() => setShowLog(!showLog)} style={{ textTransform: "none", marginBottom: 4 }}>
					{showLog ? "Hide" : "Show"} Execution Log ({report.log.length} entries)
				</Button>
				{showLog && (
					<Box
						style={{
							maxHeight: 200,
							overflow: "auto",
							backgroundColor: "#263238",
							color: "#e0e0e0",
							padding: 8,
							borderRadius: 4,
							fontSize: "0.75rem",
							fontFamily: "monospace",
						}}
					>
						{report.log.map((line, i) => (
							<div key={i}>{line}</div>
						))}
					</Box>
				)}
			</Box>
		);
	};

	const renderChainWarnings = (chainWarnings, chainLabel) => (
		<Box mb={1}>
			{chainLabel && (
				<Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 2 }}>
					{chainLabel}
				</Typography>
			)}
			{renderWarningChips(chainWarnings)}
		</Box>
	);

	// CONFIRM phase
	if (phase === "CONFIRM") {
		return (
			<Box p={2}>
				<Box display="flex" alignItems="center" style={{ marginBottom: 12 }}>
					<WarningIcon style={{ color: "#ed6c02", marginRight: 8, fontSize: "1.5rem" }} />
					<Typography variant="h6">Relay Chain Warnings</Typography>
				</Box>

				<Box style={{ backgroundColor: "#f5f5f5", padding: 12, borderRadius: 6, marginBottom: 12 }}>
					<Typography variant="body2" color="textSecondary">
						Date: {displayDate}
					</Typography>
					{!isMultiChain && truckLabel && (
						<Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 4 }}>
							<TruckIcon style={{ color: "#546e7a", fontSize: "1.1rem" }} />
							<Typography variant="body2" style={{ fontWeight: 600 }}>
								Truck: {truckLabel}
							</Typography>
						</Box>
					)}
					{isMultiChain && (
						<Typography variant="caption" color="textSecondary">
							{dayChains.length} chain{dayChains.length > 1 ? "s" : ""} with warnings on this date
						</Typography>
					)}
				</Box>

				<Alert severity="warning" style={{ marginBottom: 12 }}>
					<AlertTitle>Coverage warnings detected</AlertTitle>
					<Typography variant="body2">
						{isMultiChain
							? "Truck changes introduced coverage warnings across multiple chains on this date. You can fix them now or continue without fixing."
							: "Changing the truck introduced coverage warnings. You can fix them now or continue without fixing."}
					</Typography>
				</Alert>

				{isMultiChain
					? dayChains.map((c, idx) => (
							<Box key={idx} style={{ marginBottom: 4 }}>
								{renderChainWarnings(c.warnings || [], `Chain: ${c.truckLabel || c.chainId?.substring(0, 30)}`)}
							</Box>
					  ))
					: renderWarnings()}

				<Typography variant="body2" color="textSecondary" style={{ marginTop: 8, marginBottom: 8 }}>
					Fixing will analyse truck conflicts and partner mismatches, then reassign trucks on affected trips to resolve
					the warnings.
				</Typography>

				<FormControl component="fieldset" style={{ marginBottom: 12 }}>
					<Typography variant="caption" style={{ fontWeight: 600, marginBottom: 4 }}>
						Fix Scope
					</Typography>
					<RadioGroup value={fixScope} onChange={(e) => setFixScope(e.target.value)}>
						<FormControlLabel
							value="chain"
							control={<Radio size="small" color="primary" />}
							label={<Typography variant="body2">This chain only</Typography>}
						/>
						<FormControlLabel
							value="schedule"
							control={<Radio size="small" color="primary" />}
							label={
								<Typography variant="body2">
									All load indexes for schedule{chainScheduleName ? ` "${chainScheduleName}"` : ""} on {displayDate}
								</Typography>
							}
						/>
						<FormControlLabel
							value="date"
							control={<Radio size="small" color="primary" />}
							label={
								<Typography variant="body2">
									All chains on {displayDate}
									{hasMultipleChains ? ` (${dayChains.length} chains)` : ""}
								</Typography>
							}
						/>
					</RadioGroup>
				</FormControl>

				<Divider style={{ marginBottom: 12 }} />

				<Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
					<Button variant="outlined" onClick={onClose} size="small">
						Continue Without Fixing
					</Button>
					<Button
						variant="outlined"
						color="default"
						onClick={() => executeFix(true)}
						size="small"
						startIcon={<InfoIcon />}
					>
						Dry Run
					</Button>
					<Button
						variant="contained"
						color="secondary"
						onClick={() => executeFix(false)}
						size="small"
						startIcon={<BuildIcon />}
					>
						Fix Warnings
					</Button>
				</Box>
			</Box>
		);
	}

	// ANALYZING / FIXING phase
	if (phase === "ANALYZING" || phase === "FIXING") {
		return (
			<Box p={3} display="flex" flexDirection="column" alignItems="center">
				<CircularProgress size={40} style={{ marginBottom: 16 }} />
				<Typography variant="body1">
					{phase === "ANALYZING" ? "Analyzing chain (dry run)..." : "Fixing chain warnings..."}
				</Typography>
				<LinearProgress style={{ width: "100%", marginTop: 16 }} />
			</Box>
		);
	}

	// ERROR phase
	if (phase === "ERROR") {
		return (
			<Box p={2}>
				<Box display="flex" alignItems="center" style={{ marginBottom: 12 }}>
					<ErrorIcon style={{ color: "#f44336", marginRight: 8 }} />
					<Typography variant="h6" style={{ color: "#f44336" }}>
						Error
					</Typography>
				</Box>
				<Typography variant="body2" style={{ marginBottom: 16 }}>
					{error}
				</Typography>
				<Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
					<Button variant="outlined" onClick={onClose} size="small">
						Close
					</Button>
					<Button variant="contained" color="secondary" onClick={() => setPhase("CONFIRM")} size="small">
						Try Again
					</Button>
				</Box>
			</Box>
		);
	}

	// DRY_RUN_RESULT / RESULT phase
	const isDryRun = phase === "DRY_RUN_RESULT";
	const hasNoActions = !report?.plan?.length && !report?.actions?.length;
	const actionOnlySkipped =
		report?.actions?.length > 0 && report.actions.every((a) => a.type === "skipped" || a.type === "no_plan");

	return (
		<Box p={2}>
			<Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
				{isDryRun ? (
					<InfoIcon style={{ color: "#1976d2", marginRight: 8 }} />
				) : report?.success ? (
					<SuccessIcon style={{ color: "#4caf50", marginRight: 8 }} />
				) : hasNoActions || actionOnlySkipped ? (
					<WarningIcon style={{ color: "#ff9800", marginRight: 8 }} />
				) : (
					<ErrorIcon style={{ color: "#f44336", marginRight: 8 }} />
				)}
				<Typography variant="h6">
					{isDryRun
						? "Dry Run Result"
						: report?.success
						? "Warnings Fixed"
						: hasNoActions || actionOnlySkipped
						? "No Fixable Reassignments"
						: "Fix Completed with Issues"}
				</Typography>
			</Box>

			{report?.chain && (
				<Box style={{ backgroundColor: "#f5f5f5", padding: 8, borderRadius: 4, marginBottom: 8 }}>
					<Typography variant="caption">
						Chain: {report.chain.id?.substring(0, 40)} | Date: {report.chain.date} | Truck: {report.chain.truck}
					</Typography>
				</Box>
			)}

			{report?.warnings?.length > 0 && (
				<Box mb={1}>
					<Typography variant="caption" color="textSecondary">
						Target Chain Warnings:
					</Typography>
					{report.warnings.map((w, i) => {
						const meta = WARNING_COLORS[w.code] || { color: "#ff9800", bg: "#fff8e1" };
						return (
							<Chip
								key={i}
								size="small"
								label={w.label}
								style={{ backgroundColor: meta.bg, color: meta.color, margin: 2, fontSize: "0.7rem" }}
							/>
						);
					})}
				</Box>
			)}

			{report?.dayWarnings?.length > 0 && (
				<Box mb={1}>
					<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
						Other chains on this date:
					</Typography>
					{report.dayWarnings.map((dw, idx) => (
						<Box key={idx} style={{ marginTop: 2 }}>
							<Typography variant="caption" style={{ fontWeight: 500 }}>
								{dw.chainId?.substring(0, 30)}...
							</Typography>
							{dw.warnings.map((w, i) => {
								const meta = WARNING_COLORS[w.code] || { color: "#ff9800", bg: "#fff8e1" };
								return (
									<Chip
										key={i}
										size="small"
										label={w.label}
										style={{ backgroundColor: meta.bg, color: meta.color, margin: 2, fontSize: "0.65rem" }}
									/>
								);
							})}
						</Box>
					))}
				</Box>
			)}

			{renderPlan(report?.plan)}
			{!isDryRun && renderActions(report?.actions)}
			{!isDryRun && renderVerification(report?.verification)}

			{report?.summary && !isDryRun && (
				<Box mt={2} display="flex" style={{ gap: 12 }}>
					<Chip
						size="small"
						label={`Fixed: ${report.summary.fixed}`}
						style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
					/>
					<Chip
						size="small"
						label={`Skipped: ${report.summary.skipped}`}
						style={{ backgroundColor: "#fff3e0", color: "#e65100" }}
					/>
					<Chip
						size="small"
						label={`Failed: ${report.summary.failed}`}
						style={{ backgroundColor: "#ffebee", color: "#c62828" }}
					/>
				</Box>
			)}

			{renderLog()}

			<Divider style={{ margin: "12px 0" }} />

			<Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
				{isDryRun && (
					<>
						<Button variant="outlined" onClick={onClose} size="small">
							Cancel
						</Button>
						<Button
							variant="contained"
							color="secondary"
							onClick={() => executeFix(false)}
							size="small"
							startIcon={<BuildIcon />}
							disabled={!report?.plan?.length}
						>
							Execute Fix
						</Button>
					</>
				)}
				{!isDryRun && (
					<Button
						variant="contained"
						onClick={() => {
							onDone?.();
						}}
						size="small"
					>
						Done
					</Button>
				)}
			</Box>
		</Box>
	);
});

export default RelayChainFixWarningsView;
